import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase-auth';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { showError, showSuccess } from '../../../utils/toast';
import { UserTable } from '../../../components/admin/users/UserTable';
import { UserForm } from '../../../components/admin/users/UserForm';
import { UserFilters } from '../../../components/admin/users/UserFilters';
import { InvitationTracker } from '../../../components/admin/users/InvitationTracker';
import { PlusIcon, UsersIcon, CheckCircleIcon, XCircleIcon, EnvelopeIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import '../../../styles/portal-ui-polish.css';

export interface PlatformUser {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  role: 'owner' | 'admin' | 'member';
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  auth_user_id: string;
  notification_preferences?: {
    email_notifications?: boolean;
  };
}

interface UserStats {
  total: number;
  active: number;
  inactive: number;
  byRole: {
    owner: number;
    admin: number;
    member: number;
  };
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile, hasRole } = useAuth();
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<PlatformUser | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'owner' | 'admin' | 'member'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users');
  const [stats, setStats] = useState<UserStats>({
    total: 0,
    active: 0,
    inactive: 0,
    byRole: { owner: 0, admin: 0, member: 0 }
  });

  // Check if user is super admin
  useEffect(() => {
    if (!hasRole(['super_admin'])) {
      navigate('/portal');
    }
  }, [hasRole, navigate]);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('platform_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const usersList = data || [];
      setUsers(usersList);
      
      // Calculate stats
      const stats: UserStats = {
        total: usersList.length,
        active: usersList.filter(u => u.is_active).length,
        inactive: usersList.filter(u => !u.is_active).length,
        byRole: {
          owner: usersList.filter(u => u.role === 'owner').length,
          admin: usersList.filter(u => u.role === 'admin').length,
          member: usersList.filter(u => u.role === 'member').length,
        }
      };
      setStats(stats);
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching users:', err);
        }

      }
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' ? user.is_active : !user.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user: PlatformUser) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleCloseForm = () => {
    setShowUserForm(false);
    setEditingUser(null);
  };

  const handleUserSaved = () => {
    handleCloseForm();
    fetchUsers();
  };

  const handleToggleStatus = async (user: PlatformUser) => {
    try {
      const { error } = await supabase
        .from('platform_users')
        .update({ is_active: !user.is_active })
        .eq('id', user.id);

      if (error) throw error;
      
      showSuccess(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error toggling user status:', err);
        }

      }
      showError('Failed to update user status');
    }
  };

  const handleResetPassword = async (user: PlatformUser) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      
      showSuccess('Password reset email sent successfully');
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error sending password reset:', err);
        }

      }
      showError('Failed to send password reset email');
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'member':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Super Admin';
      case 'admin':
        return 'Advisor';
      case 'member':
        return 'Assistant';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="portal-page-bg min-h-screen">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <UsersIcon className="h-5 w-5 mr-2" />
                Portal Users
              </button>
              <button
                onClick={() => setActiveTab('invitations')}
                className={`${
                  activeTab === 'invitations'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                Invitations & Tracking
              </button>
            </nav>
          </div>

          {activeTab === 'users' ? (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="portal-card">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UsersIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Users</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="portal-card">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Active</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.active}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="portal-card">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Inactive</p>
                    <p className="text-2xl font-semibold text-gray-900">{stats.inactive}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="portal-card">
              <div className="p-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass('owner')}`}>
                      Super Admins
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{stats.byRole.owner}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass('admin')}`}>
                      Advisors
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{stats.byRole.admin}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass('member')}`}>
                      Assistants
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{stats.byRole.member}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <UserFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filterRole={filterRole}
              onRoleChange={setFilterRole}
              filterStatus={filterStatus}
              onStatusChange={setFilterStatus}
            />
            
            <button
              onClick={handleCreateUser}
              className="portal-button-primary inline-flex items-center"
            >
              <PlusIcon className="-ml-0.5 mr-2 h-5 w-5" />
              Add User
            </button>
          </div>

              {/* Users Table */}
              <UserTable
                users={filteredUsers}
                currentUserId={user?.id}
                onEdit={handleEditUser}
                onToggleStatus={handleToggleStatus}
                onResetPassword={handleResetPassword}
                getRoleBadgeClass={getRoleBadgeClass}
                getRoleDisplayName={getRoleDisplayName}
              />
            </>
          ) : (
            <InvitationTracker showAllInvitations={true} />
          )}
        </div>
      </div>

      {/* User Form Modal */}
      {showUserForm && (
        <UserForm
          user={editingUser}
          onClose={handleCloseForm}
          onSave={handleUserSaved}
        />
      )}
    </div>
  );
};

export default Users;